// Overview — the dashboard home (`/`). First thing Joel sees on every load.
//
// 2026-07-02 overhaul: neomorphic (soft-UI) surfaces + animated counters,
// glowing radial gauges, a real 14-day capture sparkline, and a live
// activity feed — under the same design constraint as ever: every visible
// number traces to a real query against an existing Reverra table. No
// fabricated "time saved" or fake confidence percentages — see
// overviewQueries.ts for the data layer, where the 2026-07-02 definition
// audit is documented (Handled = owner decisions now; capture dropped the
// dead shopify_orders channel; review-queue counts come from each
// vertical's CANONICAL table so orphaned approvals can't skew them).
//
// Sections:
//   1. Hero — greeting + unified pending-review count + two NeoGauges
//      (Captured today, Handled today) vs trailing-7d-avg
//   2. Quick stats — four MetricTiles (all live counts, self-documenting
//      tooltips)
//   3. Needs your eyes — one card per vertical, counts matching the page
//      each card links to
//   4. Capture pulse + Activity — 14-day sparkline of real ingest + the
//      latest captured/decided records
//   5. Today's flow — reuses the TodayTimeline component from Focus.tsx
//   6. Agent health strip — condensed row, links to /agents for detail
//
// Screen field audit (CLAUDE.md §19) — every field ↦ writer:
//   - Greeting / date            ← greetingForHour() / todayLabel() (PT)
//   - Owner first name           ← users.display_name via useOwnerProfile
//   - Pending review count       ← loadReviewQueue().total =
//       approvals(gmail_thread,pending) + approvals(focus_reschedule,pending)
//       + agreements(under_review, live) + commissions(validated|awaiting_approval, live)
//   - Captured gauge             ← loadCaptureStats() — created_at counts over
//       messages/emails/calls/meetings/calendar_events (PT-day window)
//   - Handled gauge              ← loadHandledStats() — approvals.decided_at
//       + meeting_action_items(status='done').updated_at (PT-day window)
//   - Tile: Email drafts         ← approvals(target_type='gmail_thread', status='pending')
//   - Tile: Action items open    ← meeting_action_items(status='open')
//   - Tile: Focus reschedules    ← approvals(target_type='focus_reschedule', status='pending')
//   - Tile: Commissions to export← commissions(processing_status='approved',
//                                   gusto_export_path IS NULL, deleted_at IS NULL)
//   - Review cards               ← loadReviewQueue() per-vertical fields (above)
//   - Capture sparkline          ← loadCaptureSeries() — real created_at buckets
//   - Activity feed              ← loadRecentActivity() — latest emails/messages/
//                                   meetings rows + approvals decisions
//   - Today's flow               ← focus_rules via the existing TodayTimeline
//   - Agent health row           ← @reverra/shared/heartbeat readAllHeartbeats + classifyHeartbeat

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  Calendar,
  CheckSquare,
  DollarSign,
  Mail,
  Radio,
  Shield,
  TrendingUp,
  Zap,
} from 'lucide-react';
import {
  classifyHeartbeat,
  readAllHeartbeats,
  type HeartbeatRow,
  type HeartbeatStatus,
} from '@reverra/shared/heartbeat/read';
import PulseDot from '@/components/shared/PulseDot';
import TodayTimeline from '@/components/focus/TodayTimeline';
import ActivityPulse from '@/components/overview/ActivityPulse';
import MetricTile from '@/components/overview/MetricTile';
import NeoCard from '@/components/overview/NeoCard';
import NeoGauge from '@/components/overview/NeoGauge';
import Sparkline from '@/components/overview/Sparkline';
import { useOwnerProfile } from '@/hooks/useOwnerProfile';
import { supabase } from '@/lib/supabase';
import {
  greetingForHour,
  loadCaptureSeries,
  loadCaptureStats,
  loadHandledStats,
  loadRecentActivity,
  loadReviewQueue,
  todayLabel,
} from '@/utils/overviewQueries';
import { humanizeDaemonName } from '@/utils/humanizeDaemonName';

// ============================================================================
// Quick-stats helpers — small dedicated count queries per stat card
// ============================================================================

async function fetchOpenActionItems(): Promise<number> {
  const { count, error } = await supabase
    .from('meeting_action_items')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'open');
  return error ? 0 : (count ?? 0);
}

async function fetchFocusRulesForToday(): Promise<
  Array<{
    id: string;
    day_of_week: number;
    start_time: string;
    end_time: string;
    timezone: string;
    label: string;
    is_active: boolean;
  }>
> {
  const { data, error } = await supabase
    .from('focus_rules')
    .select('id, day_of_week, start_time, end_time, timezone, label, is_active')
    .is('deleted_at', null);
  if (error) return [];
  return (
    (data as Array<{
      id: string;
      day_of_week: number;
      start_time: string;
      end_time: string;
      timezone: string;
      label: string;
      is_active: boolean;
    }>) ?? []
  );
}

async function fetchHeartbeats(): Promise<HeartbeatRow[]> {
  return readAllHeartbeats(supabase);
}

// Human labels for the capture-pulse channel chips (CAPTURE_TABLES keys).
const CHANNEL_LABELS: Record<string, string> = {
  emails: 'Emails',
  messages: 'Messages',
  calls: 'Calls',
  meetings: 'Meetings',
  calendar_events: 'Calendar',
};

// ============================================================================
// Page
// ============================================================================

// The four "loudest" daemons for the agent-health strip — the ones
// Joel cares about at a glance. Anything else lives on /agents.
const STRIP_DAEMONS = [
  'workers_gmail_parser',
  'workers_calendar_parser',
  'workers_focus_guardian',
  'workers_commission_agent',
] as const;

const Overview = () => {
  const navigate = useNavigate();

  // Ticking `now` once a minute so greeting + date + today-timeline drift
  // correctly across midnight / focus-block boundaries without a reload.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const captureQ = useQuery({
    queryKey: ['overview', 'capture'],
    queryFn: () => loadCaptureStats(supabase),
    refetchInterval: 60_000,
  });
  const handledQ = useQuery({
    queryKey: ['overview', 'handled'],
    queryFn: () => loadHandledStats(supabase),
    refetchInterval: 60_000,
  });
  const reviewQ = useQuery({
    queryKey: ['overview', 'review_queue'],
    queryFn: () => loadReviewQueue(supabase),
    refetchInterval: 15_000,
  });
  const openItemsQ = useQuery({
    queryKey: ['overview', 'open_action_items'],
    queryFn: fetchOpenActionItems,
    refetchInterval: 30_000,
  });
  const seriesQ = useQuery({
    queryKey: ['overview', 'capture_series'],
    queryFn: () => loadCaptureSeries(supabase),
    refetchInterval: 300_000,
  });
  const activityQ = useQuery({
    queryKey: ['overview', 'recent_activity'],
    queryFn: () => loadRecentActivity(supabase),
    refetchInterval: 30_000,
  });
  const rulesQ = useQuery({
    // Distinct key from the Focus page's ['focus_rules']: this query selects a
    // SUBSET of columns (no category_allowlist) for the timeline. Sharing the
    // key let the dashboard's partial rows populate the cache that /focus then
    // read, crashing FocusRuleCard on `category_allowlist.length` (undefined).
    // Both keys are prefix-matched by invalidateQueries(['focus_rules']).
    queryKey: ['focus_rules', 'overview'],
    queryFn: fetchFocusRulesForToday,
  });
  const heartbeatsQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: fetchHeartbeats,
    refetchInterval: 30_000,
    retry: 0,
  });

  const greeting = useMemo(() => greetingForHour(now), [now]);
  const dateLabel = useMemo(() => todayLabel(now), [now]);
  const { firstName } = useOwnerProfile();

  const review = reviewQ.data;
  const pendingTotal = review?.total ?? 0;
  const pendingBreakdown = review
    ? `${review.emailDrafts} email drafts · ${review.focusReschedules} focus reschedules · ${review.agreementRedlines} agreement redlines · ${review.commissionsAwaitingApproval} commissions awaiting approval`
    : '';

  const todayCapture = captureQ.data?.today;
  const channelChips = useMemo(
    () =>
      Object.entries(todayCapture?.byTable ?? {})
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1]),
    [todayCapture],
  );

  const heartbeatRows = heartbeatsQ.data ?? [];
  const classified = useMemo(
    () =>
      heartbeatRows.map((row) => ({
        row,
        status: row.payload
          ? classifyHeartbeat(row.payload, now, { daemonName: row.daemon_name }).status
          : null,
      })),
    [heartbeatRows, now],
  );
  const aliveCount = classified.filter((c) => c.status === 'alive').length;
  const totalDaemons = classified.length;

  return (
    <div className="space-y-9">
      {/* ============================ */}
      {/* Hero                          */}
      {/* ============================ */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8"
      >
        <div className="flex-1 min-w-0">
          <h1 className="text-4xl lg:text-[46px] font-serif font-semibold tracking-tight text-foreground leading-tight">
            {greeting}, <span className="text-gradient-forest">{firstName}</span>.
          </h1>
          <p className="mt-3 text-muted-foreground text-[15px] leading-relaxed max-w-2xl">
            {dateLabel}
            {' · '}
            {pendingTotal === 0 ? (
              <>Nothing waiting on you right now. The agents are working.</>
            ) : (
              <span title={pendingBreakdown}>
                You have{' '}
                <span className="text-attention font-semibold">
                  {pendingTotal} item{pendingTotal === 1 ? '' : 's'}
                </span>{' '}
                awaiting your review. Everything else is handled.
              </span>
            )}
          </p>
        </div>

        {/* Two-gauge stat card */}
        <NeoCard className="flex items-center gap-7 px-8 py-6 shrink-0">
          <NeoGauge
            value={captureQ.data?.today.total ?? 0}
            average={captureQ.data?.sevenDayAvgPerDay ?? 0}
            label="Captured"
            definition="Rows captured today (PT) across emails, messages, calls, meetings and calendar events — vs the trailing-7-day daily average."
            tone="forest"
          />
          <div className="w-px h-24 bg-border" aria-hidden />
          <NeoGauge
            value={handledQ.data?.today.total ?? 0}
            average={handledQ.data?.sevenDayAvgPerDay ?? 0}
            label="Handled"
            definition="Decisions you made today (PT): approvals decided + action items marked done — vs the trailing-7-day daily average."
            tone="sage"
          />
        </NeoCard>
      </motion.section>

      {/* ============================ */}
      {/* Quick stats                   */}
      {/* ============================ */}
      <section
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        aria-label="Awaiting review breakdown"
      >
        <MetricTile
          icon={Mail}
          label="Email drafts"
          value={review?.emailDrafts}
          definition="Pending email-draft approvals (approvals: target_type='gmail_thread', status='pending') — same count as the Inbox queue."
          onClick={() => navigate('/inbox')}
          attention
          index={0}
        />
        <MetricTile
          icon={CheckSquare}
          label="Action items open"
          value={openItemsQ.data}
          definition="Open action items extracted from meetings (meeting_action_items: status='open')."
          onClick={() => navigate('/action-items')}
          index={1}
        />
        <MetricTile
          icon={Shield}
          label="Focus reschedules"
          value={review?.focusReschedules}
          definition="Pending focus-reschedule proposals (approvals: target_type='focus_reschedule', status='pending') — same count as the Focus page."
          onClick={() => navigate('/focus')}
          attention
          index={2}
        />
        <MetricTile
          icon={DollarSign}
          label="Commissions to export"
          value={review?.commissionsToExport}
          definition="Approved commission periods whose Gusto CSV hasn't shipped yet (commissions: processing_status='approved', no gusto_export_path)."
          onClick={() => navigate('/commissions')}
          index={3}
        />
      </section>

      {/* ============================ */}
      {/* Needs your eyes              */}
      {/* ============================ */}
      <section>
        <div className="flex items-center gap-3 mb-5">
          <Zap className="w-4 h-4 text-attention" aria-hidden />
          <h2 className="text-lg font-serif font-semibold text-foreground">Needs your eyes</h2>
          <div className="flex-1 h-px bg-border ml-2" />
        </div>
        {pendingTotal === 0 ? (
          <NeoCard variant="inset" className="p-5">
            <p className="text-sm text-muted-foreground">
              Approval queue is clear. The agents will surface new items as soon as they need your
              judgment.
            </p>
          </NeoCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <ReviewCard
              title="Email drafts"
              count={review?.emailDrafts ?? 0}
              caption="Voice-matched replies waiting to be approved or edited"
              href="/inbox"
              index={0}
            />
            <ReviewCard
              title="Focus reschedules"
              count={review?.focusReschedules ?? 0}
              caption="Events overlapping your focus blocks — you decide which move"
              href="/focus"
              index={1}
            />
            <ReviewCard
              title="Agreement redlines"
              count={review?.agreementRedlines ?? 0}
              caption="Agreements under review with drafted redlines awaiting you"
              href="/agreements"
              index={2}
            />
            <ReviewCard
              title="Commissions"
              count={review?.commissionsAwaitingApproval ?? 0}
              caption="Validated commission periods awaiting your approval"
              href="/commissions"
              index={3}
            />
          </div>
        )}
      </section>

      {/* ============================ */}
      {/* Capture pulse + Activity      */}
      {/* ============================ */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <NeoCard className="lg:col-span-3 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2.5">
              <TrendingUp className="w-4 h-4 text-forest/70" aria-hidden />
              <h2 className="text-sm font-serif font-semibold text-foreground">Capture pulse</h2>
              <span className="text-[11px] text-muted-foreground">last 14 days</span>
            </div>
            <span className="text-[11px] font-mono text-muted-foreground">
              {seriesQ.data ? `${seriesQ.data.total.toLocaleString()} captured` : '…'}
            </span>
          </div>
          <Sparkline values={seriesQ.data?.counts ?? []} days={seriesQ.data?.days} height={72} />
          <div
            className="mt-3 flex items-center gap-2 flex-wrap"
            aria-label="Captured today by channel"
          >
            {channelChips.length === 0 ? (
              <span className="text-[11px] text-muted-foreground">Nothing captured yet today</span>
            ) : (
              channelChips.map(([table, n]) => (
                <span
                  key={table}
                  className="neo-chip px-2.5 py-1 text-[11px] text-foreground/80 inline-flex items-center gap-1.5"
                >
                  <span className="font-mono font-semibold text-forest">{n}</span>
                  {CHANNEL_LABELS[table] ?? table} today
                </span>
              ))
            )}
          </div>
        </NeoCard>

        <NeoCard className="lg:col-span-2 p-5">
          <div className="flex items-center gap-2.5 mb-3">
            <Radio className="w-4 h-4 text-forest/70" aria-hidden />
            <h2 className="text-sm font-serif font-semibold text-foreground">Live activity</h2>
          </div>
          <ActivityPulse events={activityQ.data ?? []} now={now} />
        </NeoCard>
      </section>

      {/* ============================ */}
      {/* Today's flow                  */}
      {/* ============================ */}
      <section>
        <div className="flex items-center gap-3 mb-5">
          <Calendar className="w-4 h-4 text-muted-foreground" aria-hidden />
          <h2 className="text-lg font-serif font-semibold text-foreground">Today&apos;s flow</h2>
          <div className="flex-1 h-px bg-border ml-2" />
        </div>
        <NeoCard variant="inset" className="p-4">
          <TodayTimeline rules={rulesQ.data ?? []} displayTz="America/Los_Angeles" />
        </NeoCard>
      </section>

      {/* ============================ */}
      {/* Agent health strip            */}
      {/* ============================ */}
      <Link
        to="/agents"
        className="block group focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40 rounded-[20px]"
      >
        <NeoCard hover className="px-5 py-3.5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Activity className="w-3.5 h-3.5" aria-hidden />
              <span>Agent health</span>
              {totalDaemons > 0 && (
                <span className="font-mono text-foreground">
                  · {aliveCount}/{totalDaemons} alive
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {STRIP_DAEMONS.map((name) => {
                const entry = classified.find((c) => c.row.daemon_name === name);
                return (
                  <DaemonChip
                    key={name}
                    daemonName={name}
                    status={entry?.status ?? null}
                    payloadPresent={!!entry?.row.payload}
                  />
                );
              })}
              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1 ml-1 group-hover:text-foreground transition-colors">
                Open details
                <ArrowRight className="w-3 h-3" aria-hidden />
              </span>
            </div>
          </div>
        </NeoCard>
      </Link>
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

function ReviewCard(props: {
  title: string;
  count: number;
  caption: string;
  href: string;
  index: number;
}): JSX.Element {
  const dimmed = props.count === 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 * props.index, ease: [0.4, 0, 0.2, 1] }}
    >
      <Link
        to={props.href}
        className="block h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40 rounded-[20px]"
        aria-label={`${props.title}: ${props.count} pending`}
      >
        <div className={`neo-raised-hover h-full p-5 ${dimmed ? 'opacity-70' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-foreground">{props.title}</span>
            <span
              className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-xs font-bold ${
                dimmed
                  ? 'neo-chip text-muted-foreground'
                  : 'bg-attention text-primary-foreground neo-pulse-glow'
              }`}
            >
              {props.count}
            </span>
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed">{props.caption}</p>
          <div
            className={`mt-3 flex items-center gap-1 text-[13px] font-medium text-forest transition-opacity ${
              dimmed ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            Review <ArrowRight className="w-3.5 h-3.5" aria-hidden />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function DaemonChip(props: {
  daemonName: string;
  status: HeartbeatStatus | null;
  payloadPresent: boolean;
}): JSX.Element {
  const label = humanizeDaemonName(props.daemonName);
  // If the daemon hasn't reported (no row) we still render the chip in
  // an "unknown" tone — surfaces a missing daemon clearly instead of
  // hiding it.
  const dotColor =
    // 'asleep' is green with 'alive': a capture daemon on a shut laptop is the
    // system working as designed. Falling through to amber would make the
    // Overview disagree with the sidebar chip every morning.
    props.status === 'alive' || props.status === 'asleep'
      ? 'green'
      : props.status === 'warning'
        ? 'amber'
        : props.status === 'alert'
          ? 'red'
          : 'amber';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] text-foreground/80"
      title={
        props.payloadPresent ? `${label}: ${props.status ?? 'unknown'}` : `${label}: not reporting`
      }
    >
      <PulseDot color={dotColor} />
      <span className="font-medium">{label}</span>
    </span>
  );
}

export default Overview;
